import * as React from 'react'
import {makeCancelable, isCanceled} from '@freckle/cancelable-promise'
import type {CancelablePromise} from '@freckle/cancelable-promise'
import {type ResourceStatusT} from '@freckle/resource-status'
import {exhaustive} from '@freckle/exhaustive'
import {
  type CallbackFn,
  type ExtraDeps,
  useExtraDeps,
  type PrimitiveDep,
  useSafeEffect
} from '@freckle/react-hooks'
import {useSafeCallback} from '@freckle/react-hooks'
import {type RlDomainIdT} from '@freckle/student-entities/ts/common/types/rl-domain-id'
import {type RlSkillUspIdT} from '@freckle/student-entities/ts/common/types/rl-skill-usp-id'
import {type RlStandardIdT} from '@freckle/student-entities/ts/common/types/rl-standard-id'
import {type RlSubSkillUspIdT} from '@freckle/student-entities/ts/common/types/rl-subskill-usp-id'
import {type LangT} from '@freckle/student-materials/src/helpers/languages'

type FreckleOpaquePrimitiveDep =
  // Opaque types that wrap primitives
  LangT | RlDomainIdT | RlStandardIdT | RlSkillUspIdT | RlSubSkillUspIdT

type FrecklePrimitiveDep = PrimitiveDep | FreckleOpaquePrimitiveDep

type UseAsyncBailTag = '_use-async-bail'
const USE_ASYNC_BAIL_TAG: UseAsyncBailTag = '_use-async-bail'

type UseAsyncBail<R> = {
  tag: UseAsyncBailTag
  resourceStatus: ResourceStatusT<R>
}

const mkBail = <R>(status: ResourceStatusT<R>): Promise<UseAsyncBail<R>> => {
  return Promise.resolve({
    tag: USE_ASYNC_BAIL_TAG,
    resourceStatus: status
  })
}

const isUseAsyncBail = <R>(d: UseAsyncBail<R> | R): d is UseAsyncBail<R> => {
  if (typeof d === 'object' && d !== null) {
    // @ts-expect-error: checking if tag exists on object
    return Object.prototype.hasOwnProperty.call(d, 'tag') && d.tag === USE_ASYNC_BAIL_TAG
  }
  return false
}

export function useAsync<R>(
  promise: (
    bailWithStatus: (r: ResourceStatusT<R>) => Promise<UseAsyncBail<R>>
  ) => Promise<R | UseAsyncBail<R>>,
  deps: ReadonlyArray<FrecklePrimitiveDep>
): {
  resource: ResourceStatusT<R>
  reload: CallbackFn<() => void>
} {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useAsyncExtraDeps(() => promise(mkBail), deps, {})
}

export function useAsyncExtraDeps<R, T extends Record<string, unknown>>(
  promise: (
    a: T,
    bailWithStatus: (r: ResourceStatusT<R>) => Promise<UseAsyncBail<R>>
  ) => Promise<R | UseAsyncBail<R>>,
  deps: ReadonlyArray<FrecklePrimitiveDep>,
  extraDeps: {[P in keyof T]: T[P] extends infer D ? ExtraDeps<D> : never}
): {
  resource: ResourceStatusT<R>
  reload: CallbackFn<() => void>
} {
  const [resourceStatus, setResourceStatus] = React.useState<ResourceStatusT<R>>({status: 'idle'})
  const [cancelable, setCancelable] = React.useState<CancelablePromise<R | UseAsyncBail<R>> | null>(
    null
  )
  //at this point we know we satisfy primitives dep and only casting to `any` because @freckle/react-hooks
  // doesn't know about our Freckle-specific Opaque primitive types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const primitiveDeps: any = deps
  const {extraDepValues, allDeps} = useExtraDeps(primitiveDeps, extraDeps)

  const load = useSafeCallback(
    () => async () => {
      switch (resourceStatus.status) {
        case 'complete':
        case 'reloading':
        case 'updating':
        case 'updating-error':
          setResourceStatus({status: 'reloading', data: resourceStatus.data})
          break
        case 'error':
        case 'idle':
        case 'loading':
          setResourceStatus({status: 'loading'})
          break
        default:
          exhaustive(resourceStatus)
      }

      try {
        const cancelable = makeCancelable(promise(extraDepValues as T, mkBail))
        setCancelable(cancelable)
        const data = await cancelable.promise
        if (isUseAsyncBail(data)) {
          setResourceStatus(data.resourceStatus)
        } else {
          setResourceStatus({status: 'complete', data, hasUpdated: false})
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (!isCanceled(error)) {
          setResourceStatus({status: 'error', error})
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    allDeps
  )

  useSafeEffect(() => {
    load()
  }, [load])

  React.useEffect(() => {
    return () => {
      if (cancelable !== null && cancelable !== undefined) {
        cancelable.cancel()
      }
    }
  }, [cancelable])

  // Avoid exposing loading promise; hook users should observe resourceStatus
  const reload = useSafeCallback<() => void>(
    () => () => {
      load()
    },
    [load]
  )

  return {resource: resourceStatus, reload}
}
